/**
 * Shared constants for tenant + sub-tenant config editors and the
 * tenant creation wizard. Keeps option lists, capability definitions,
 * and default config buckets in one place so the editor surfaces stay
 * in lockstep with each other.
 */

// Legacy exports — kept for backward compatibility with any code that
// imports VOICE_MODEL_OPTIONS / TEXT_MODEL_OPTIONS directly. New code
// should read from PROVIDERS[provider].voiceModels etc.
export const VOICE_MODEL_OPTIONS = [
  { value: "gemini-3.1-flash-live-preview", label: "gemini-3.1-flash-live-preview (newest)" },
  { value: "gemini-live-2.5-flash-native-audio", label: "gemini-live-2.5-flash-native-audio" },
  { value: "gemini-live-2.5-flash", label: "gemini-live-2.5-flash" },
];
export const TEXT_MODEL_OPTIONS = [
  { value: "gemini-3.1-flash-lite", label: "gemini-3.1-flash-lite (newest, cheapest)" },
  { value: "gemini-3-flash", label: "gemini-3-flash (new, balanced)" },
  { value: "gemini-2.5-flash-lite", label: "gemini-2.5-flash-lite (cheapest)" },
  { value: "gemini-2.5-flash", label: "gemini-2.5-flash (balanced)" },
  { value: "gemini-2.5-pro", label: "gemini-2.5-pro (best quality)" },
];

// ── Cartesia Sonic voices ────────────────────────────────────────
// These are used by the STT→LLM→TTS (pipeline) provider when the user
// picks "Cartesia" as the TTS backend. Values are Cartesia voice UUIDs.
// Full catalog: https://play.cartesia.ai/voices (use "Copy Voice ID").
// Curated short list — add more here and they'll appear in the dropdown.
export const CARTESIA_VOICE_OPTIONS = [
  { value: "794f9389-aac1-45b6-b726-9d9369183238", label: "Sarah — warm, conversational female" },
  { value: "bf0a246a-8642-498a-9950-80c35e9276b5", label: "Savannah — professional female" },
  { value: "a0e99841-438c-4a64-b679-ae501e7d6091", label: "Barbershop Man — friendly male" },
  { value: "8832a0b5-47b2-4751-bb22-6a8e2149303d", label: "David — mature, professional male" },
  { value: "95856005-0332-41b0-935f-352e296aa0df", label: "Classy British Man" },
  { value: "156fb8d2-335b-4950-9cb3-a2d33befec77", label: "Helpful Woman — calm, clear" },
  { value: "41534e16-2966-4c6b-9670-111411def906", label: "1920s Radio Man" },
  { value: "f091cfa2-b67f-4e5f-bee2-5f5d0f2e1be6", label: "British Reading Lady" },
];

// Cartesia STT + TTS models. Operators rarely need to touch these but
// exposing them lets a forward-looking admin try newer ink-whisper / sonic
// releases without a code change.
export const CARTESIA_TTS_MODELS = [
  { value: "sonic-2", label: "Sonic 2 (newest, recommended)" },
  { value: "sonic",   label: "Sonic (legacy)" },
];
export const CARTESIA_STT_MODELS = [
  { value: "ink-whisper", label: "Ink-Whisper (default)" },
];

// Full Gemini voice catalog — 30 voices. Descriptions from Google AI Studio.
// Preview: https://aistudio.google.com/app/live
export const VOICE_NAME_OPTIONS = [
  { id: "Achernar",      desc: "Soft and contemplative" },
  { id: "Achird",        desc: "Friendly and warm" },
  { id: "Algenib",       desc: "Gravelly and assertive" },
  { id: "Algieba",       desc: "Smooth and polished" },
  { id: "Alnilam",       desc: "Firm and authoritative" },
  { id: "Aoede",         desc: "Breezy and natural" },
  { id: "Autonoe",       desc: "Bright and confident" },
  { id: "Callirhoe",     desc: "Easy-going and relaxed" },
  { id: "Charon",        desc: "Informative and clear" },
  { id: "Despina",       desc: "Smooth and confident" },
  { id: "Enceladus",     desc: "Calm and balanced" },
  { id: "Erinome",       desc: "Clear and focused" },
  { id: "Fenrir",        desc: "Excitable and dynamic" },
  { id: "Gacrux",        desc: "Mature and composed" },
  { id: "Iapetus",       desc: "Clear and direct" },
  { id: "Kore",          desc: "Firm and confident" },
  { id: "Laomedeia",     desc: "Upbeat and bubbly" },
  { id: "Leda",          desc: "Youthful and energetic" },
  { id: "Orus",          desc: "Firm and decisive" },
  { id: "Puck",          desc: "Upbeat and energetic" },
  { id: "Pulcherrima",   desc: "Calm and measured" },
  { id: "Rasalgethi",    desc: "Informative and assured" },
  { id: "Sadachbia",     desc: "Lively and expressive" },
  { id: "Sadaltager",    desc: "Knowledgeable and clear" },
  { id: "Schedar",       desc: "Even and composed" },
  { id: "Sulafar",       desc: "Warm and engaging" },
  { id: "Umbriel",       desc: "Easy-going and thoughtful" },
  { id: "Vindemiatrix",  desc: "Gentle and nurturing" },
  { id: "Zephyr",        desc: "Bright and cheerful" },
  { id: "Zubenelgenubi", desc: "Casual and conversational" },
];

// ─── Provider catalog ─────────────────────────────────────────────
// Single source of truth for provider-specific model/voice options.
// The LLM tab reads from PROVIDERS[selectedProvider] to render the
// right dropdowns. Adding a new provider = one entry here + the
// runtime implementation in configural-runtime/src/providers/.

export const OPENAI_VOICE_OPTIONS = [
  { id: "alloy",   desc: "Neutral and balanced" },
  { id: "ash",     desc: "Warm and conversational" },
  { id: "ballad",  desc: "Expressive and melodic" },
  { id: "coral",   desc: "Clear and engaging" },
  { id: "echo",    desc: "Smooth and resonant" },
  { id: "fable",   desc: "Warm and British-tinged" },
  { id: "nova",    desc: "Friendly and upbeat" },
  { id: "onyx",    desc: "Deep and authoritative" },
  { id: "sage",    desc: "Calm and measured" },
  { id: "shimmer", desc: "Bright and expressive" },
  { id: "verse",   desc: "Versatile and natural" },
];

export const PROVIDERS = {
  gemini: {
    label: "Google Gemini",
    voiceModels: [
      { value: "gemini-3.1-flash-live-preview", label: "Gemini 3.1 Flash Live (preview, newest)" },
      { value: "gemini-live-2.5-flash-native-audio", label: "Gemini 2.5 Flash Native Audio" },
      { value: "gemini-live-2.5-flash", label: "Gemini 2.5 Flash" },
    ],
    textModels: [
      { value: "gemini-3.1-flash-lite", label: "Gemini 3.1 Flash Lite (newest, cheapest)" },
      { value: "gemini-3-flash", label: "Gemini 3 Flash (new, balanced)" },
      { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
      { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
      { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (best quality)" },
    ],
    voices: VOICE_NAME_OPTIONS,
    defaultVoiceModel: "gemini-3.1-flash-live-preview",
    defaultTextModel: "gemini-2.5-flash",
    defaultVoice: "Aoede",
    voicePreviewUrl: "https://aistudio.google.com/app/live",
  },
  pipeline: {
    label: "Pipeline (STT → LLM → TTS)",
    voiceModels: [
      { value: "pipeline-google", label: "Google STT + TTS (Standard)" },
    ],
    textModels: [
      { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite (cheapest)" },
      { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (balanced)" },
      { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (best)" },
      { value: "gpt-5.1", label: "GPT-5.1 (OpenAI flagship)" },
      { value: "gpt-5.4-nano", label: "GPT-5.4 Nano (newest, cheapest OpenAI)" },
      { value: "gpt-4o-mini", label: "GPT-4o Mini" },
      { value: "gpt-4o", label: "GPT-4o" },
      { value: "deepseek-chat", label: "DeepSeek V3.2 (chat)" },
      { value: "deepseek-reasoner", label: "DeepSeek R1 (reasoner)" },
      { value: "qwen3-next-80b-instruct", label: "Qwen 3 Next 80B (via Vertex)" },
      { value: "qwen3-235b-a22b-instruct", label: "Qwen 3 235B (via Vertex)" },
    ],
    voices: [
      // Neural2 — best quality ($16/1M chars)
      { id: "en-US-Neural2-A", desc: "Female, Neural2 ★" },
      { id: "en-US-Neural2-C", desc: "Female, Neural2 (warm) ★" },
      { id: "en-US-Neural2-D", desc: "Male, Neural2 ★" },
      { id: "en-US-Neural2-E", desc: "Female, Neural2 (bright) ★" },
      { id: "en-US-Neural2-F", desc: "Female, Neural2 (calm) ★" },
      { id: "en-US-Neural2-G", desc: "Female, Neural2 (clear) ★" },
      { id: "en-US-Neural2-H", desc: "Female, Neural2 (soft) ★" },
      { id: "en-US-Neural2-I", desc: "Male, Neural2 (warm) ★" },
      { id: "en-US-Neural2-J", desc: "Male, Neural2 (deep) ★" },
      // WaveNet — high quality ($16/1M chars)
      { id: "en-US-Wavenet-A", desc: "Male, WaveNet" },
      { id: "en-US-Wavenet-B", desc: "Male, WaveNet (warm)" },
      { id: "en-US-Wavenet-C", desc: "Female, WaveNet" },
      { id: "en-US-Wavenet-D", desc: "Male, WaveNet (deep)" },
      { id: "en-US-Wavenet-E", desc: "Female, WaveNet (bright)" },
      { id: "en-US-Wavenet-F", desc: "Female, WaveNet (calm)" },
      { id: "en-US-Wavenet-G", desc: "Female, WaveNet (clear)" },
      { id: "en-US-Wavenet-H", desc: "Female, WaveNet (soft)" },
      { id: "en-US-Wavenet-I", desc: "Male, WaveNet (clear)" },
      { id: "en-US-Wavenet-J", desc: "Male, WaveNet (bright)" },
      // Standard — cheapest ($4/1M chars)
      { id: "en-US-Standard-A", desc: "Male, Standard" },
      { id: "en-US-Standard-B", desc: "Male, Standard (warm)" },
      { id: "en-US-Standard-C", desc: "Female, Standard" },
      { id: "en-US-Standard-D", desc: "Male, Standard (deep)" },
      { id: "en-US-Standard-E", desc: "Female, Standard (bright)" },
      { id: "en-US-Standard-F", desc: "Female, Standard (calm)" },
      { id: "en-US-Standard-G", desc: "Female, Standard (clear)" },
      { id: "en-US-Standard-H", desc: "Female, Standard (soft)" },
      { id: "en-US-Standard-I", desc: "Male, Standard (clear)" },
      { id: "en-US-Standard-J", desc: "Male, Standard (deep)" },
    ],
    defaultVoiceModel: "pipeline-google",
    defaultTextModel: "gemini-2.5-flash",
    defaultVoice: "en-US-Neural2-C",
    voicePreviewUrl: "https://cloud.google.com/text-to-speech#section-2",
  },
  chain: {
    label: "Chain (sequential agents)",
    voiceModels: [
      { value: "chain-gemini-3.1-flash-live", label: "Chain + Gemini 3.1 Flash Live (newest)" },
      { value: "chain-gemini",       label: "Chain + Gemini 2.5 Live (native voice)" },
      { value: "chain-openai",       label: "Chain + OpenAI Realtime" },
      { value: "chain-pipeline",     label: "Chain + STT → LLM → TTS (pipeline)" },
    ],
    textModels: [
      { value: "gemini-3.1-flash-lite-preview", label: "Gemini 3.1 Flash Lite Preview (newest)" },
      { value: "gemini-3.1-flash-lite", label: "Gemini 3.1 Flash Lite" },
      { value: "gemini-3-flash",        label: "Gemini 3 Flash (new)" },
      { value: "gemini-2.5-flash",      label: "Gemini 2.5 Flash" },
      { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite (fastest, cheapest)" },
      { value: "gemini-2.5-pro",        label: "Gemini 2.5 Pro" },
      { value: "gpt-5.1",               label: "GPT-5.1 (OpenAI flagship)" },
      { value: "gpt-5.4-nano",          label: "GPT-5.4 Nano (newest, cheapest OpenAI)" },
      { value: "gpt-4o-mini",           label: "GPT-4o Mini" },
      { value: "gpt-4o",                label: "GPT-4o" },
      { value: "deepseek-chat",         label: "DeepSeek V3.2 (chat)" },
      { value: "deepseek-reasoner",     label: "DeepSeek R1 (reasoner)" },
    ],
    voices: VOICE_NAME_OPTIONS, // legacy — prefer voicesForChain(voiceModel)
    defaultVoiceModel: "chain-gemini",
    defaultTextModel: "gemini-2.5-flash",
    defaultVoice: "Aoede",
    voicePreviewUrl: "https://aistudio.google.com/app/live",
  },
  openai: {
    label: "OpenAI",
    voiceModels: [
      { value: "gpt-4o-mini-realtime-preview", label: "GPT-4o Mini Realtime Preview" },
      { value: "gpt-4o-realtime-preview", label: "GPT-4o Realtime Preview" },
      { value: "gpt-realtime", label: "GPT Realtime (GA — recommended)" },
    ],
    textModels: [
      { value: "gpt-5.1", label: "GPT-5.1 (flagship)" },
      { value: "gpt-5.4-nano", label: "GPT-5.4 Nano (newest, cheapest)" },
      { value: "gpt-4o-mini", label: "GPT-4o Mini" },
      { value: "gpt-4o", label: "GPT-4o (balanced)" },
      { value: "o3-mini", label: "o3-mini (reasoning)" },
    ],
    voices: OPENAI_VOICE_OPTIONS,
    defaultVoiceModel: "gpt-4o-mini-realtime-preview",  // switch to "gpt-realtime" for GA
    defaultTextModel: "gpt-4o-mini",
    defaultVoice: "coral",
    voicePreviewUrl: "https://platform.openai.com/docs/guides/text-to-speech",
  },
};

// Resolve which voice catalog + preview url + sensible default apply for a
// given chain voice_model. The chain provider supports 3 audio backends
// (gemini-live, openai realtime, STT→LLM→TTS pipeline) — each has its own
// voice namespace. This lets the LLM tab's Voice dropdown switch options
// when the Voice Model changes.
export function voicesForChain(voiceModel) {
  if (voiceModel === "chain-openai") {
    return {
      voices: OPENAI_VOICE_OPTIONS,
      defaultVoice: "coral",
      previewUrl: "https://platform.openai.com/docs/guides/text-to-speech",
      previewLabel: "OpenAI Docs",
    };
  }
  if (voiceModel === "chain-pipeline") {
    return {
      voices: PROVIDERS.pipeline.voices,
      defaultVoice: PROVIDERS.pipeline.defaultVoice,
      previewUrl: PROVIDERS.pipeline.voicePreviewUrl,
      previewLabel: "Google Cloud TTS",
    };
  }
  // default: chain-gemini
  return {
    voices: VOICE_NAME_OPTIONS,
    defaultVoice: "Aoede",
    previewUrl: "https://aistudio.google.com/app/live",
    previewLabel: "Google AI Studio",
  };
}

export const SAFETY_LEVEL_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "strict", label: "Strict" },
  { value: "permissive", label: "Permissive" },
];

// Full set of languages supported by the Gemini Live API — mirrors
// Sandy's Agents.jsx list so both admins accept the same detect_language
// values. Display label doubles as the stored value.
export const LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "French",
  "Portuguese",
  "German",
  "Italian",
  "Dutch",
  "Russian",
  "Japanese",
  "Korean",
  "Mandarin Chinese",
  "Cantonese",
  "Hindi",
  "Bengali",
  "Urdu",
  "Punjabi",
  "Tamil",
  "Telugu",
  "Gujarati",
  "Marathi",
  "Arabic",
  "Turkish",
  "Vietnamese",
  "Thai",
  "Indonesian",
  "Malay",
  "Filipino",
  "Polish",
  "Ukrainian",
  "Romanian",
  "Czech",
  "Swedish",
  "Norwegian",
  "Danish",
  "Finnish",
  "Greek",
  "Hebrew",
  "Persian",
  "Swahili",
  "Amharic",
  "Somali",
  "Haitian Creole",
].map((l) => ({ value: l, label: l }));

// Built-in capability defs for the Config tab. Some capabilities are
// gated on infrastructure that isn't wired up yet (telephony, warm
// handoff registry); they render as locked toggles with a "Requires X"
// note so authors know what's coming.
export const CAPABILITY_DEFS = [
  {
    id: "auto_close_call",
    label: "Auto-close call",
    description: "Automatically disconnect after the agent says goodbye.",
  },
  {
    id: "language_detection",
    label: "Language preference",
    description:
      "Detect non-English speech and offer to switch languages for the rest of the call.",
  },
  {
    id: "barge_in",
    label: "Barge-in (interruption handling)",
    description:
      "Stop the agent's audio immediately when the patient starts speaking. Allows natural turn-taking.",
  },
  {
    id: "active_listening",
    label: "Active listening",
    description:
      "Pause and stay silent when the patient is mid-sentence, thinking, or has more to say.",
  },
  {
    id: "warm_handoff",
    label: "Warm handoff",
    description:
      "Hand the call over to a different agent configuration mid-conversation.",
    locked: "Requires additional setup",
  },
  {
    id: "live_transfer",
    label: "Live transfer",
    description:
      "Transfer the call to a phone number (nurse station, after-hours line, specialist).",
    locked: "Requires telephony (Twilio/SIP)",
  },
  {
    id: "voicemail_awareness",
    label: "Voicemail awareness",
    description:
      "Detect when an outbound call reaches voicemail and leave a message or hang up.",
    locked: "Requires telephony",
  },
];

// Defaults used when a config has empty buckets — every field the
// Config tab edits has a fallback so the form always renders.
export const DEFAULT_LLM_CONFIG = {
  // Provider is implicitly "chain" — the only mode the platform supports.
  // The UI no longer exposes a provider selector; new tenants always start in chain mode.
  provider: "chain",
  voice_model: "chain-gemini",
  text_model: "gemini-2.5-flash",
  voice: "Achernar",
  temperature: 0.1,
  max_output_tokens: 8192,
  safety_level: "default",
  max_call_duration: 600,
  timeout_warning_at: 540,
  voice_pacing_instructions:
    "Speak at a calm, measured pace. Do not rush. Pause briefly between sentences.",
  handoff_mode: "lazy",
  noise_suppression: false,
  mic_noise_threshold: 0.003,

  // Bandwidth Extension (neural 8→16 kHz upsampler for Twilio).
  // Requires onnxruntime-node + ONNX model at BWE_MODEL_PATH or ./models/bwe.onnx.
  // Pass-through on browser audio (already wideband).
  twilio_bwe_enabled: false,
  twilio_bwe_frame_size: 320,

  // DeepFilterNet (neural noise suppression + mild dereverb). Preserves
  // speech phonemes better than RNNoise. Can chain with or replace RNNoise.
  // Requires onnxruntime-node + model at DFN_MODEL_PATH or ./models/deepfilternet3.onnx.
  deepfilternet_enabled: false,

  // AGC (Automatic Gain Control) — normalizes inbound speech level before
  // it reaches the LLM's audio encoder. Applied AFTER noise suppression.
  // Off by default; enable per tenant when phone callers vary widely in volume.
  agc_enabled: false,
  agc_target_dbfs: -20,         // target RMS level (−24 quieter, −16 louder)
  agc_noise_floor_dbfs: -45,    // below this, gain is frozen (prevents hiss amplification)
  agc_limiter_dbfs: -1,         // peak limiter ceiling
  agc_attack_ms: 10,            // how fast gain rises on loud input
  agc_release_ms: 800,          // how slowly gain decays on quiet input
  agc_max_gain_db: 20,          // max boost applied (prevents runaway amplification)
  // Phone (Twilio) voice-activity gate — server-side, applied in
  // configural-runtime's twilio-bridge.js. Separate from mic_noise_threshold,
  // which only runs in the browser AudioWorklet.
  phone_vad_enabled: true,
  phone_vad_open_threshold: 0.008,  // RMS to open the gate
  phone_vad_close_threshold: 0.003, // RMS to keep it open (hysteresis)
  phone_vad_hangover_ms: 200,       // how long to stay open after speech

  // Twilio outbound speech rate. The bridge resamples agent audio so Twilio
  // plays it at this multiple of real-time speed. 1.0 = natural rate; values
  // below 1.0 slow the agent down (useful for older patients or poor lines).
  // Range: 0.7–1.1.
  twilio_playback_rate: 0.82,

  // Adaptive playback rate — automatic slowdowns during the call.
  // Off by default so existing tenants keep fixed 0.82x behavior.
  twilio_adaptive_age_enabled: false,       // slow down based on patient age
  twilio_adaptive_trouble_enabled: false,   // slow down when patient signals trouble
  twilio_adaptive_min_rate: 0.70,           // floor for adaptive slowdowns
  twilio_adaptive_trouble_step: 0.04,       // per-signal decrement

  // Silence re-prompt — agent re-engages if patient is silent after a turn.
  // Runtime timer (not in prompt). Used by chain provider for both voice & text.
  silence_reprompt: {
    enabled: true,
    timeout_seconds: 15,
    text: "Are you still there? I didn't catch that — could you repeat?",
    max_attempts: 2,
  },

  // Output filter — runs every agent's response text through a small LLM
  // BEFORE it reaches the caller (TTS in pipeline mode, or as a sentence-
  // level guardrail in voice mode). Catches hallucinated dates/times,
  // out-of-scope medical advice, and PII leaks.
  filter_llm: {
    enabled: false,
    model: "gemini-2.5-flash-lite",     // small/fast: ~150-300ms per check
    mode: "rewrite",                    // "rewrite" (let it edit), "block" (only allow/reject), "log" (log only, never modify)
    instructions:
      "You are a safety + accuracy filter. Allow the message if it is appropriate, factually consistent with prior tool results, and within the agent's scope. Rewrite ONLY if the message contains: a hallucinated date/time not present in tool results; medical advice beyond appointment/triage scope; specific drug dosing recommendations; or PII that wasn't necessary to repeat back. Otherwise return the message unchanged.",
    apply_to_agents: [],                // empty = all agents; or list ids to scope (e.g. ["triage", "scheduling"])
    timeout_ms: 2000,                   // skip filter if it doesn't return in time
  },
};

export const DEFAULT_SYSTEM_TOOLS = {
  auto_close_call: true,
  language_detection: true,
  barge_in: true,
  active_listening: true,
  warm_handoff: false,
  live_transfer: false,
  voicemail_awareness: false,
};

export const DEFAULT_VARIABLES = {
  default_language: "English",
  brand_voice: "",
  // Practice identity
  practice_name: "",
  practice_short_name: "",
  // Contact
  practice_phone: "",
  after_hours_phone: "",
  practice_address: "",
  practice_city_state: "",
  // Hours
  clinic_hours: "",
  // Agent persona
  agent_persona_name: "your care coordinator",
  agent_persona_description: "care coordinator",
  agent_gender: "neutral",
  // Timezone
  timezone: "America/New_York",
};

// Variable definitions for the UI editor — grouped into sections.
// Each entry: { key, label, placeholder, type ("text"|"textarea"), section }
export const VARIABLE_DEFINITIONS = [
  // Identity
  { key: "practice_name", label: "Practice Name", placeholder: "Metro Health Oncology Center", type: "text", section: "Identity" },
  { key: "practice_short_name", label: "Practice Short Name", placeholder: "Metro Oncology", type: "text", section: "Identity", help: "Used in greeting and casual references" },
  // Contact
  { key: "practice_phone", label: "Main Phone", placeholder: "555-200-1000", type: "text", section: "Contact" },
  { key: "after_hours_phone", label: "After-Hours Phone", placeholder: "555-200-1001", type: "text", section: "Contact" },
  { key: "practice_address", label: "Address", placeholder: "100 Medical Center Dr, Suite 200", type: "text", section: "Contact" },
  { key: "practice_city_state", label: "City, State", placeholder: "Houston, TX", type: "text", section: "Contact" },
  // Hours
  { key: "clinic_hours", label: "Clinic Hours", placeholder: "Monday-Friday, 8 AM - 5 PM", type: "text", section: "Hours" },
  // Agent Persona
  { key: "agent_persona_name", label: "Agent Name / Title", placeholder: "your care coordinator", type: "text", section: "Agent Persona", help: "How the agent introduces itself (e.g. 'Sarah' or 'your care coordinator')" },
  { key: "agent_persona_description", label: "Agent Description", placeholder: "care coordinator", type: "text", section: "Agent Persona", help: "Short role description (e.g. 'experienced oncology nurse')" },
  { key: "agent_gender", label: "Agent Gender", placeholder: "neutral", type: "select", section: "Agent Persona", help: "Helps the LLM use correct pronouns. Options: male, female, neutral", options: ["neutral", "female", "male"] },
  // Phone numbers moved to Telephony sub-tab (not in Variables)
  // Timezone
  { key: "timezone", label: "Timezone", placeholder: "America/New_York", type: "select", section: "Timezone", help: "Used for current date/time in prompts and call scheduling", options: ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix", "America/Anchorage", "Pacific/Honolulu", "America/Puerto_Rico", "UTC"] },
  // Language & Voice
  { key: "default_language", label: "Default Language", placeholder: "English", type: "text", section: "Language & Voice" },
  { key: "brand_voice", label: "Brand Voice", placeholder: "Warm, professional, empathetic", type: "textarea", section: "Language & Voice", help: "Personality instructions applied to all agents" },
];
